<template>
  <el-dialog
    v-model="visible"
    :title="isResultView ? '' : '录入终端设备'"
    width="640px"
    :close-on-click-modal="false"
    :show-close="true"
    class="pre-dialog"
    @close="handleClose"
  >
    <!-- 录入前 -->
    <template v-if="!resultData.length">
      <div class="dialog-hero">
        <div class="hero-icon-wrap">
          <el-icon :size="22" color="#fff"><Plus /></el-icon>
        </div>
        <div class="hero-text">
          <h3>{{ isAppendMode ? "追加设备到现有批次" : "录入新设备" }}</h3>
          <p>
            {{
              isAppendMode
                ? "向当前批次追加新设备，共享同一套证书"
                : "填写设备硬件编号，支持批量录入，自动生成 endpointId"
            }}
          </p>
        </div>
      </div>

      <!-- 追加模式下显示批次号 -->
      <div v-if="isAppendMode" class="batch-tag-display">
        <span class="batch-tag-label">目标证书批次</span>
        <span class="batch-tag-code">证书 {{ batchCode }}</span>
      </div>

      <el-form v-if="!isAppendMode" label-position="top" class="device-form">
        <el-form-item label="备注（可选）">
          <el-input
            v-model="remark"
            placeholder="如：第一批船载终端"
            clearable
          />
        </el-form-item>
      </el-form>

      <div class="device-list-wrap">
        <div class="list-header">
          <span>设备列表</span>
          <span class="list-count">{{ deviceRows.length }} 台</span>
        </div>

        <div
          v-for="(row, idx) in deviceRows"
          :key="idx"
          class="device-input-row"
        >
          <div class="row-index">{{ idx + 1 }}</div>
          <el-input
            v-model="row.deviceId"
            placeholder="设备ID（硬件唯一编号）"
            size="default"
            class="row-input"
          />
          <el-input
            v-model="row.name"
            placeholder="名称（可选）"
            size="default"
            class="row-input-name"
          />
          <el-button
            type="danger"
            :icon="Delete"
            circle
            size="default"
            :disabled="deviceRows.length <= 1"
            @click="removeRow(idx)"
          />
        </div>

        <el-button class="add-btn" plain @click="addRow">
          <el-icon><Plus /></el-icon>
          <span>添加一行</span>
        </el-button>
      </div>

      <div class="tips">
        <div class="tip-row">
          <span class="tip-icon">·</span>设备ID填 IMEI / MAC / SN 等硬件唯一号
        </div>
        <div class="tip-row">
          <span class="tip-icon">·</span>设备ID不能重复，每台必须不同
        </div>
        <div class="tip-row">
          <span class="tip-icon">·</span
          >录入即激活，设备连接后自动上线，无需额外操作
        </div>
      </div>
    </template>

    <!-- 录入后：结果 -->
    <template v-else>
      <div class="result-wrap">
        <div class="result-summary" :class="resultSummaryClass">
          <el-icon :size="32" :color="resultIconColor">
            <CircleCheckFilled v-if="successCount > 0" />
            <WarningFilled v-else />
          </el-icon>
          <div class="result-info">
            <div class="result-title">
              录入{{ successCount > 0 ? "完成" : "异常" }}
            </div>
            <div class="result-sub">
              共 {{ resultData.length }} 台 · 成功 {{ successCount }} · 失败
              {{ failureCount }}
            </div>
          </div>
        </div>

        <table class="result-table">
          <thead>
            <tr>
              <th>deviceId</th>
              <th>endpointId</th>
              <th style="width: 70px; text-align: center">状态</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in resultData"
              :key="i"
              :class="{ 'row-fail': !r.success }"
            >
              <td class="cell-deviceId">
                <span v-if="r.success" class="dot dot-success"></span>
                <span v-else class="dot dot-fail"></span>
                {{ r.deviceId }}
              </td>
              <td>
                <span v-if="r.success" class="endpoint-id">{{
                  r.endpointId
                }}</span>
                <span v-else class="error-msg">{{ r.errorMessage }}</span>
              </td>
              <td style="text-align: center">
                <span
                  class="status-badge"
                  :class="r.success ? 'badge-success' : 'badge-fail'"
                >
                  {{ r.success ? "成功" : "失败" }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template #footer>
      <template v-if="!resultData.length">
        <el-button size="large" @click="visible = false">取消</el-button>
        <el-button
          type="primary"
          size="large"
          :loading="submitting"
          class="submit-btn"
          @click="handleSubmit"
        >
          <el-icon><Check /></el-icon>
          <span>确认录入</span>
        </el-button>
      </template>
      <template v-else>
        <el-button
          type="primary"
          size="large"
          class="submit-btn"
          @click="closeDialog"
          >完成</el-button
        >
      </template>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { ElMessage } from "element-plus";
import {
  Plus,
  Delete,
  Check,
  CircleCheckFilled,
  WarningFilled,
} from "@element-plus/icons-vue";
import { preRegisterDevicesApi } from "@/api/cert/certBatchApi";

const props = defineProps<{
  modelValue: boolean;
  certBatchId?: string;
  batchCode?: string;
}>();
const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  success: [];
}>();

const visible = ref(props.modelValue);
const remark = ref("");
const submitting = ref(false);
const isAppendMode = computed(() => !!props.certBatchId);
const deviceRows = ref<{ deviceId: string; name: string }[]>([
  { deviceId: "", name: "" },
]);
const resultData = ref<any[]>([]);
const isResultView = computed(() => resultData.value.length > 0);

const successCount = computed(
  () => resultData.value.filter((r) => r.success).length
);
const failureCount = computed(
  () => resultData.value.filter((r) => !r.success).length
);
const resultSummaryClass = computed(() =>
  failureCount.value === 0 ? "summary-success" : "summary-fail"
);
const resultIconColor = computed(() =>
  failureCount.value === 0 ? "#10b981" : "#f59e0b"
);

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val;
    if (val) {
      deviceRows.value = [{ deviceId: "", name: "" }];
      resultData.value = [];
      remark.value = "";
    }
  }
);
watch(visible, (val) => emit("update:modelValue", val));

const addRow = () => {
  deviceRows.value.push({ deviceId: "", name: "" });
};

const removeRow = (index: number) => {
  deviceRows.value.splice(index, 1);
};

const handleClose = () => {
  deviceRows.value = [{ deviceId: "", name: "" }];
  resultData.value = [];
};

const closeDialog = () => {
  visible.value = false;
};

const handleSubmit = async () => {
  const validRows = deviceRows.value.filter((r) => r.deviceId.trim());
  if (validRows.length === 0) {
    ElMessage.warning("请至少输入一条设备ID");
    return;
  }

  const ids = validRows.map((r) => r.deviceId.trim());
  const dup = ids.filter((id, i) => ids.indexOf(id) !== i);
  if (dup.length > 0) {
    ElMessage.warning(`设备ID重复: ${dup.join(", ")}`);
    return;
  }

  submitting.value = true;
  try {
    const results = await preRegisterDevicesApi(
      validRows.map((r) => ({
        deviceId: r.deviceId.trim(),
        name: r.name.trim() || undefined,
      })),
      isAppendMode.value ? undefined : remark.value || undefined,
      props.certBatchId || undefined
    );

    if (results) {
      const success = results.filter((r: any) => r.success).length;
      const fail = results.filter((r: any) => !r.success).length;
      if (success > 0) {
        ElMessage.success(
          `成功录入 ${success} 台终端${fail > 0 ? `，${fail} 台失败` : ""}`
        );
      } else {
        ElMessage.warning("录入全部失败，请检查终端编号");
      }
      emit("success");
      visible.value = false;
    }
  } catch (e: any) {
    ElMessage.error(e.message || "录入失败");
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
:deep(.pre-dialog .el-dialog__header) {
  padding: 20px 24px 0;
}
:deep(.pre-dialog .el-dialog__body) {
  padding: 16px 24px;
}
:deep(.pre-dialog .el-dialog__footer) {
  padding: 12px 24px 20px;
  border-top: 1px solid #f1f5f9;
}

/* 顶部英雄区 */
.dialog-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  margin-bottom: 20px;
}
.hero-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}
.hero-text h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
}
.hero-text p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

/* 表单 */
:deep(.device-form .el-form-item__label) {
  font-weight: 600;
  color: #475569;
  padding-bottom: 6px;
}
:deep(.device-form .el-input__wrapper) {
  border-radius: 8px;
}

/* 批次标签显示 */
.batch-tag-display {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 10px;
  margin-bottom: 16px;
  border-left: 3px solid #3b82f6;
}
.batch-tag-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.batch-tag-code {
  font-size: 14px;
  font-weight: 700;
  color: #1e3a8a;
  font-family: "SF Mono", Monaco, monospace;
}

/* 设备列表 */
.device-list-wrap {
  margin-top: 20px;
}
.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 10px;
}
.list-count {
  font-size: 12px;
  color: #3b82f6;
  background: #dbeafe;
  padding: 2px 10px;
  border-radius: 12px;
}
.device-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.row-index {
  width: 24px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.row-input {
  flex: 1;
}
.row-input :deep(.el-input__wrapper) {
  border-radius: 8px;
}
.row-input-name {
  width: 160px;
}
.row-input-name :deep(.el-input__wrapper) {
  border-radius: 8px;
}
.add-btn {
  width: 100%;
  margin-top: 8px;
  border-style: dashed;
  border-radius: 8px;
  height: 36px;
  color: #64748b;
}

/* 提示 */
.tips {
  margin-top: 20px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 10px;
  border-left: 3px solid #94a3b8;
}
.tip-row {
  font-size: 12px;
  color: #64748b;
  line-height: 1.9;
}
.tip-icon {
  color: #94a3b8;
  margin-right: 6px;
  font-weight: 700;
}

/* 结果 */
.result-wrap {
  padding: 4px;
}
.result-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 16px;
}
.result-summary.summary-success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}
.result-summary.summary-fail {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}
.result-info {
  flex: 1;
}
.result-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2px;
}
.result-sub {
  font-size: 13px;
  color: #64748b;
}

/* 结果表 */
.result-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.result-table th {
  text-align: left;
  padding: 10px 12px;
  background: #fafbfc;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 1px solid #e8ecf1;
}
.result-table td {
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
}
.result-table tr.row-fail {
  background: #fef2f2;
}
.result-table tr:last-child td {
  border-bottom: none;
}
.cell-deviceId {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "SF Mono", Monaco, monospace;
  font-weight: 600;
}
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-success {
  background: #10b981;
}
.dot-fail {
  background: #ef4444;
}
.endpoint-id {
  font-family: "SF Mono", Monaco, monospace;
  color: #3b82f6;
  font-weight: 600;
}
.error-msg {
  color: #dc2626;
  font-size: 12px;
}

/* 状态徽章 */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.badge-success {
  background: #d1fae5;
  color: #047857;
}
.badge-fail {
  background: #fee2e2;
  color: #b91c1c;
}

/* 提交按钮 */
.submit-btn {
  border-radius: 10px !important;
  padding: 0 28px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg, #3b82f6, #6366f1) !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
}
.submit-btn:hover {
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.4) !important;
}
</style>
